import { z } from 'zod'

import { type Brand, BrandSchema } from '~/@entities/Brand'
import type {
  AddWorksheetAssignScheduleTargetResponse,
  CommonCloudFrontUrlResponse,
  CommonResponseCollectionWorksheetDesignTypeResponse,
  CommonResponseListAssignResponse,
  CommonResponseSliceAcademyWorksheetResponse,
  CommonResponseSliceArithmeticWorksheetResponse,
  CommonResponseSliceChapterWorksheetResponse,
  CommonResponseSliceEntranceWorksheetResponse,
  CommonResponseSliceExamWorksheetResponse,
  CommonResponseSliceGroupedArithmeticWorksheetResponse,
  CommonResponseSlicePracticeTestWorksheetResponse,
  CommonResponseSliceSchoolPrepareTextbookWorksheetResponse,
  CommonResponseSliceSchoolPrepareWorksheetResponse,
  CommonResponseSliceWeeklyWorksheetResponse,
  CommonResponseSliceWorksheetDesignResponse,
  CommonResponseWorksheetDesignDetailResponse,
  CopyCustomWorksheetData,
  CreateWorksheetAssignScheduleResponse,
  HwpPurchaseHistoryResponse,
  HwpUnitPriceResponse,
  OriginalWorksheetHwpDownloadQuotaResponse,
  OriginalWorksheetQuotaResponse,
  SchoolTestSimilarWorksheetCountResponse,
  SchoolTestSimilarWorksheetResponse,
  SliceSatWorksheetResponse,
  SliceSchoolPrepareEssayWorksheetResponse,
  SliceUniversityPracticeTestWorksheetResponse,
  SliceWorksheetAssignScheduleDetailResponse,
  WorksheetAssignScheduleCountResponse,
  WorksheetAssignScheduleTargetResponse,
  WorksheetCaptchaChallengeResponse,
  WorksheetCaptchaVerifyResponse,
  WorksheetHwpCreationResponse,
  WorksheetHwpDownloadResponse,
  WorksheetResponse,
} from '~/api-spec/__generated__/main'
import type { WorksheetSearchResponse } from '~/api-spec/__generated__/search'
import { parseApiResponse } from '~/schema/apiBoundary'
import { type Slice, sliceSchema } from '~/schema/slice'

import {
  type ArithmeticWorksheet,
  type ArithmeticWorksheetGroup,
  ArithmeticWorksheetGroupSchema,
  ArithmeticWorksheetSchema,
  type LeveledWorksheetListItem,
  LeveledWorksheetListItemSchema,
  type MyWorksheetListItem,
  MyWorksheetListItemSchema,
  type OriginalWorksheetQuota,
  OriginalWorksheetQuotaSchema,
  type PracticeTestWorksheetListItem,
  PracticeTestWorksheetListItemSchema,
  type SatWorksheetListItem,
  SatWorksheetListItemSchema,
  type SchoolTestSimilarWorksheet,
  type SchoolTestSimilarWorksheetCount,
  SchoolTestSimilarWorksheetCountSchema,
  SchoolTestSimilarWorksheetSchema,
  type SharedCustomWorksheet,
  SharedCustomWorksheetSchema,
  type TextbookWorksheetListItem,
  TextbookWorksheetListItemSchema,
  type Worksheet,
  type WorksheetAssignee,
  WorksheetAssigneeSchema,
  type WorksheetAssignSchedule,
  type WorksheetAssignScheduleCount,
  WorksheetAssignScheduleCountSchema,
  type WorksheetAssignScheduleCreation,
  WorksheetAssignScheduleCreationSchema,
  WorksheetAssignScheduleSchema,
  type WorksheetAssignScheduleTarget,
  WorksheetAssignScheduleTargetSchema,
  type WorksheetCaptchaChallenge,
  WorksheetCaptchaChallengeSchema,
  type WorksheetCaptchaVerification,
  WorksheetCaptchaVerificationSchema,
  type WorksheetDesign,
  type WorksheetDesignDetail,
  WorksheetDesignDetailSchema,
  WorksheetDesignSchema,
  type WorksheetDesignTemplate,
  WorksheetDesignTemplateSchema,
  type WorksheetFile,
  WorksheetFileSchema,
  type WorksheetHwpFile,
  WorksheetHwpFileSchema,
  type WorksheetHwpPurchaseHistory,
  WorksheetHwpPurchaseHistorySchema,
  type WorksheetHwpQuota,
  WorksheetHwpQuotaSchema,
  type WorksheetHwpRequest,
  WorksheetHwpRequestSchema,
  type WorksheetHwpUnitPrice,
  WorksheetHwpUnitPriceSchema,
  WorksheetSchema,
} from './schemas'

const ENTITY = 'Worksheet'

// 스펙이 실제 응답과 다른 곳. 2026-10-08 백엔드 origin/develop(08ff3525b) 의 응답 클래스로 확인.
//   1. 배정 학생(`/worksheet/{id}/assign`, 목록 탭마다의 assignList) — 서버에 AssignResponse 가 둘(studentWorksheet·
//      worksheet 패키지)이라 springdoc 이 동명 클래스를 하나로 합쳤다. 학습지 쪽은 worksheet 패키지 클래스를 내서
//      studentStatus 가 오고 score·studentHomeworkId·essayScoringOpened 는 오지 않는데 스펙은 반대로 그린다
//      (worksheet/controller/response/AssignResponse.kt)
//   2. 연산 묶음(`/worksheet/arithmetic/group`) — 중·고등이면 ArithmeticWorksheet 의 하위 클래스
//      AvailableArithmeticWorksheet 가 학습지 메타와 assignList 를 더 싣는데 스펙은 상위 클래스만 그린다
//      (worksheet/controller/WorksheetPresenterForTab.kt)
// 스키마가 실제 모양을 검증하므로 입력은 생성 타입으로 받고 이 한 줄에서만 unknown 을 거친다.
const asWire = (raw: unknown): unknown => raw

export const adaptWorksheet = (raw: WorksheetResponse | undefined): Worksheet =>
  parseApiResponse(WorksheetSchema, raw, ENTITY)

export const adaptWorksheetId = (raw: CopyCustomWorksheetData): Worksheet['id'] =>
  parseApiResponse(WorksheetSchema.shape.id, raw, ENTITY)

export const adaptWorksheetFile = (raw: CommonCloudFrontUrlResponse): WorksheetFile =>
  parseApiResponse(WorksheetFileSchema, raw, ENTITY)

export const adaptWorksheetDesignTemplates = (
  raw: CommonResponseCollectionWorksheetDesignTypeResponse['data'],
): WorksheetDesignTemplate[] =>
  parseApiResponse(z.array(WorksheetDesignTemplateSchema), raw, ENTITY)

export const adaptWorksheetDesigns = (
  raw: CommonResponseSliceWorksheetDesignResponse['data'],
): Slice<WorksheetDesign> => parseApiResponse(sliceSchema(WorksheetDesignSchema), raw, ENTITY)

export const adaptWorksheetDesignDetail = (
  raw: CommonResponseWorksheetDesignDetailResponse['data'],
): WorksheetDesignDetail => parseApiResponse(WorksheetDesignDetailSchema, raw, ENTITY)

export const adaptWorksheetHwpRequest = (raw: WorksheetHwpCreationResponse): WorksheetHwpRequest =>
  parseApiResponse(WorksheetHwpRequestSchema, raw, ENTITY)

export const adaptWorksheetHwpFiles = (raw: WorksheetHwpDownloadResponse): WorksheetHwpFile[] =>
  parseApiResponse(z.object({ files: z.array(WorksheetHwpFileSchema) }), raw, ENTITY)?.files

export const adaptOriginalWorksheetQuota = (
  raw: OriginalWorksheetQuotaResponse,
): OriginalWorksheetQuota => parseApiResponse(OriginalWorksheetQuotaSchema, raw, ENTITY)

// 생성 타입과 실제 와이어가 어긋나는 필드:
//   한도의 limitType·단가의 discountLabel — 생성 타입은 optional(키 생략)로 그리지만 서버는 키를 빼지 않고 null 을 싣는다
//   구매 내역의 items — 생성기가 내부 클래스 이름(Item)을 다른 응답의 Item 과 섞어 엉뚱한 모양으로 그린다
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 worksheet/controller/WorksheetQuotaController.kt 와
// controller/response/OriginalWorksheetHwpDownloadQuotaResponse.kt·HwpUnitPriceResponse.kt·HwpPurchaseHistoryResponse.kt 로 확인.
// 스키마가 실제 와이어를 검증하므로 입력을 이 한 자리에서 unknown 으로 넘긴다.
const asWireHwp = (
  raw:
    | OriginalWorksheetHwpDownloadQuotaResponse
    | HwpUnitPriceResponse
    | HwpPurchaseHistoryResponse,
): unknown => raw

export const adaptWorksheetHwpQuota = (
  raw: OriginalWorksheetHwpDownloadQuotaResponse,
): WorksheetHwpQuota => parseApiResponse(WorksheetHwpQuotaSchema, asWireHwp(raw), ENTITY)

export const adaptWorksheetHwpUnitPrice = (raw: HwpUnitPriceResponse): WorksheetHwpUnitPrice =>
  parseApiResponse(WorksheetHwpUnitPriceSchema, asWireHwp(raw), ENTITY)

export const adaptWorksheetHwpPurchaseHistory = (
  raw: HwpPurchaseHistoryResponse,
): WorksheetHwpPurchaseHistory =>
  parseApiResponse(WorksheetHwpPurchaseHistorySchema, asWireHwp(raw), ENTITY)

export const adaptWorksheetCaptchaChallenge = (
  raw: WorksheetCaptchaChallengeResponse,
): WorksheetCaptchaChallenge => parseApiResponse(WorksheetCaptchaChallengeSchema, raw, ENTITY)

export const adaptWorksheetCaptchaVerification = (
  raw: WorksheetCaptchaVerifyResponse,
): WorksheetCaptchaVerification => parseApiResponse(WorksheetCaptchaVerificationSchema, raw, ENTITY)

export const adaptWorksheetAssignees = (
  raw: CommonResponseListAssignResponse['data'],
): WorksheetAssignee[] => parseApiResponse(z.array(WorksheetAssigneeSchema), asWire(raw), ENTITY)

/***************** 선생님 학습지 목록 *****************/

export const adaptMyWorksheets = (
  raw: CommonResponseSliceAcademyWorksheetResponse['data'],
): Slice<MyWorksheetListItem> =>
  parseApiResponse(sliceSchema(MyWorksheetListItemSchema), asWire(raw), ENTITY)

export const adaptLeveledWorksheets = (
  raw:
    | CommonResponseSliceSchoolPrepareWorksheetResponse['data']
    | SliceSchoolPrepareEssayWorksheetResponse
    | CommonResponseSliceEntranceWorksheetResponse['data']
    | CommonResponseSliceChapterWorksheetResponse['data']
    | CommonResponseSliceWeeklyWorksheetResponse['data']
    | CommonResponseSliceExamWorksheetResponse['data'],
): Slice<LeveledWorksheetListItem> =>
  parseApiResponse(sliceSchema(LeveledWorksheetListItemSchema), asWire(raw), ENTITY)

export const adaptTextbookWorksheets = (
  raw: CommonResponseSliceSchoolPrepareTextbookWorksheetResponse['data'],
): Slice<TextbookWorksheetListItem> =>
  parseApiResponse(sliceSchema(TextbookWorksheetListItemSchema), asWire(raw), ENTITY)

export const adaptSatWorksheets = (raw: SliceSatWorksheetResponse): Slice<SatWorksheetListItem> =>
  parseApiResponse(sliceSchema(SatWorksheetListItemSchema), asWire(raw), ENTITY)

export const adaptPracticeTestWorksheets = (
  raw:
    | CommonResponseSlicePracticeTestWorksheetResponse['data']
    | SliceUniversityPracticeTestWorksheetResponse,
): Slice<PracticeTestWorksheetListItem> =>
  parseApiResponse(sliceSchema(PracticeTestWorksheetListItemSchema), asWire(raw), ENTITY)

export const adaptArithmeticWorksheets = (
  raw: CommonResponseSliceArithmeticWorksheetResponse['data'],
): Slice<ArithmeticWorksheet> =>
  parseApiResponse(sliceSchema(ArithmeticWorksheetSchema), raw, ENTITY)

export const adaptArithmeticWorksheetGroups = (
  raw: CommonResponseSliceGroupedArithmeticWorksheetResponse['data'],
): Slice<ArithmeticWorksheetGroup> =>
  parseApiResponse(sliceSchema(ArithmeticWorksheetGroupSchema), asWire(raw), ENTITY)

export const adaptSchoolTestSimilarWorksheets = (
  raw: SchoolTestSimilarWorksheetResponse[],
): SchoolTestSimilarWorksheet[] =>
  parseApiResponse(z.array(SchoolTestSimilarWorksheetSchema), asWire(raw), ENTITY)

export const adaptSchoolTestSimilarWorksheetCount = (
  raw: SchoolTestSimilarWorksheetCountResponse,
): SchoolTestSimilarWorksheetCount =>
  parseApiResponse(SchoolTestSimilarWorksheetCountSchema, raw, ENTITY)

/***************** 예약 출제 *****************/

export const adaptWorksheetAssignScheduleTargets = (
  raw: WorksheetAssignScheduleTargetResponse[],
): WorksheetAssignScheduleTarget[] =>
  parseApiResponse(z.array(WorksheetAssignScheduleTargetSchema), raw, ENTITY)

export const adaptWorksheetAssignSchedules = (
  raw: SliceWorksheetAssignScheduleDetailResponse,
): Slice<WorksheetAssignSchedule> =>
  parseApiResponse(sliceSchema(WorksheetAssignScheduleSchema), raw, ENTITY)

export const adaptWorksheetAssignScheduleCount = (
  raw: WorksheetAssignScheduleCountResponse,
): WorksheetAssignScheduleCount => parseApiResponse(WorksheetAssignScheduleCountSchema, raw, ENTITY)

export const adaptWorksheetAssignScheduleCreation = (
  raw: CreateWorksheetAssignScheduleResponse,
): WorksheetAssignScheduleCreation =>
  parseApiResponse(WorksheetAssignScheduleCreationSchema, raw, ENTITY)

export const adaptWorksheetAssignScheduleTargetCount = (
  raw: AddWorksheetAssignScheduleTargetResponse,
): Pick<WorksheetAssignScheduleCreation, 'targetCount'> =>
  parseApiResponse(WorksheetAssignScheduleCreationSchema.pick({ targetCount: true }), raw, ENTITY)

// 검색 서버는 Spring Slice 모양을 봉투 없이 그대로 낸다. 앱은 content 와 last 만 읽는다.
export const adaptSharedCustomWorksheets = (
  raw: WorksheetSearchResponse,
): { content: SharedCustomWorksheet[]; last: boolean } => {
  const page = parseApiResponse(
    z.object({ content: z.array(SharedCustomWorksheetSchema), last: z.boolean() }),
    raw,
    ENTITY,
  )
  return { content: page?.content ?? [], last: page?.last ?? true }
}

// 브랜드 조회 응답은 2026-11-02 기준 dev swagger(/v3/api-docs/all)에 아직 실리지 않아 생성 타입이 없다.
// 서버 develop 402d2c846 의 WorksheetBrandController·WorksheetBrandResponse 로 와이어 모양을 확인했고,
// CommonResponse 봉투 없이 { brand } 를 그대로 보낸다. 재생성되면 입력을 생성 타입으로 바꾼다.
const WorksheetBrandWireSchema = z.object({ brand: BrandSchema.nullable() })

/** 학습지만으로 브랜드를 알 수 없거나(매쓰플랫 제공 학습지) 모르는 값이 오면 null */
export const adaptWorksheetBrand = (raw: unknown): Brand | null => {
  const brand = BrandSchema.safeParse(
    parseApiResponse(WorksheetBrandWireSchema, raw, ENTITY)?.brand,
  )
  return brand.success ? brand.data : null
}
